import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';

import { s, brand, ink, shadow, radius, white } from '../theme';

/**
 * Label + hint + error presentation wrapped around any control.
 *
 * Ported from `Program Entry Updates/src/components/ui/Field.tsx`. The control itself is
 * passed as children so the existing widgets (antd pickers, react-select, ReactQuill,
 * the `reuse/FormControls.js` inputs) keep working unchanged.
 *
 * `error` is the plain "this required field is empty" flag; `issue` is a richer finding
 * from validation.js carrying its own message and severity. When both are present the
 * issue wins, because it says something more specific.
 */
const Field = ({
    label,
    htmlFor,
    required,
    hint,
    help,
    error,
    issue,
    className = '',
    children,
}) => {
    const showRequiredError = error && !issue;

    return (
        <div className={className}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginBottom: '6px',
                }}
            >
                <label htmlFor={htmlFor} style={s.label}>
                    {label}
                    {required ? (
                        <span style={{ marginLeft: '2px', color: brand[600] }} aria-hidden="true">
                            *
                        </span>
                    ) : ''}
                    {required ? <span style={s.srOnly}> (required)</span> : ''}
                </label>
                {help ? <FieldHelp help={help} /> : ''}
            </div>

            {children}

            {hint && !showRequiredError && !issue ? <p style={s.hint}>{hint}</p> : ''}

            {showRequiredError ? (
                <p style={s.errorText}>
                    <AlertTriangle size={12} style={{ marginTop: '1px', flexShrink: 0 }} aria-hidden="true" />
                    This field is required.
                </p>
            ) : ''}

            {issue ? (
                <p style={issue.severity === 'error' ? s.errorText : s.warningText}>
                    <AlertTriangle size={12} style={{ marginTop: '1px', flexShrink: 0 }} aria-hidden="true" />
                    {issue.message}
                </p>
            ) : ''}
        </div>
    );
};

/**
 * Hover tooltip for the optional `help` text. Kept local to Field — nothing else needs it.
 * Visibility is toggled in state rather than via `:hover`, since there is no stylesheet
 * to hang a pseudo-class off.
 */
const FieldHelp = ({ help }) => {
    const [open, setOpen] = React.useState(false);

    return (
        <span
            style={{ position: 'relative', display: 'inline-flex' }}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <Info size={14} color={ink[400]} aria-hidden="true" />
            <span style={s.srOnly}>{help}</span>
            {open ? (
                <span
                    role="tooltip"
                    style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        marginBottom: '8px',
                        width: '208px',
                        zIndex: 20,
                        borderRadius: radius.lg,
                        backgroundColor: ink[900],
                        color: white,
                        padding: '6px 10px',
                        fontSize: '11px',
                        fontWeight: 500,
                        lineHeight: 1.4,
                        boxShadow: shadow.tooltip,
                        pointerEvents: 'none',
                    }}
                >
                    {help}
                </span>
            ) : ''}
        </span>
    );
};

export default Field;
